import MuiTextField from '@mui/material/TextField';
import { useCallback, useMemo } from 'react';

import useValidate from '@maya/hooks/useValidate';

import type { ChangeEvent, ChangeEventHandler, FC } from 'react';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';

export interface TextFieldProps extends Omit<MuiTextFieldProps, 'onChange' | 'defaultValue'> {
  pattern?: string;
  required?: boolean;
  readOnly?: boolean;
  defaultValue?: string | undefined | null;
  onChange: (newValue: string, event: ChangeEvent<HTMLInputElement>) => void;
  onValidate?: (valid: boolean) => void;
}

const TextField: FC<TextFieldProps> = ({
  required = false,
  readOnly = false,
  pattern,
  error,
  helperText,
  variant = 'filled',
  fullWidth = true,
  defaultValue,
  onChange,
  onValidate,
  InputProps,
  ...props
}) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultValueMemoized = useMemo(() => defaultValue, []);

  const { validate, hint, hasError } = useValidate({
    defaultValue: defaultValueMemoized,
    required,
    pattern,
    error,
    helperText,
    onValidate
  });

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const value = event.target.value;
      onChange(value, event);
      validate(value);
    },
    [onChange, validate]
  );

  return (
    <MuiTextField
      required={required}
      variant={variant}
      fullWidth={fullWidth}
      onChange={handleOnChange}
      helperText={hint ?? <>&nbsp;</>}
      defaultValue={defaultValueMemoized}
      error={hasError}
      InputProps={{
        readOnly,
        ...InputProps
      }}
      {...props}
    />
  );
};

export default TextField;
